// CryptoCorrelation.js - Crypto Correlation Matrix
// Uses sparkline data from /top endpoint - NO EXTRA API CALLS!
import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Loader,
  Info,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  HelpCircle,
  BarChart3,
  Grid3X3
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const CryptoCorrelation = () => {
  const [cryptos, setCryptos] = useState([]);
  const [correlationMatrix, setCorrelationMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPair, setSelectedPair] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [displayCount, setDisplayCount] = useState(10); // Show top 10 by default

  useEffect(() => {
    fetchCryptoData();
  }, []);

  useEffect(() => {
    if (cryptos.length > 0) {
      calculateCorrelations();
    }
  }, [cryptos, displayCount]);

  const fetchCryptoData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/crypto/top?limit=20`);
      if (res.ok) {
        const data = await res.json();
        // Filter to only cryptos with sparkline data
        const withSparklines = data.filter(c => c.sparkline && c.sparkline.length > 10);
        setCryptos(withSparklines);
      } else {
        throw new Error('Failed to fetch crypto data');
      }
    } catch (err) {
      console.error('Error fetching crypto data:', err);
      setError('Failed to load cryptocurrency data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate Pearson correlation coefficient between two arrays
  const calculatePearsonCorrelation = (x, y) => {
    if (!x || !y || x.length === 0 || y.length === 0) return 0;
    
    // Use the shorter length to ensure equal arrays
    const n = Math.min(x.length, y.length);
    if (n < 5) return 0; // Need at least 5 points
    
    const xSlice = x.slice(0, n);
    const ySlice = y.slice(0, n);
    
    // Calculate means
    const meanX = xSlice.reduce((a, b) => a + b, 0) / n;
    const meanY = ySlice.reduce((a, b) => a + b, 0) / n;
    
    // Calculate covariance and standard deviations
    let covariance = 0;
    let stdX = 0;
    let stdY = 0;
    
    for (let i = 0; i < n; i++) {
      const diffX = xSlice[i] - meanX;
      const diffY = ySlice[i] - meanY;
      covariance += diffX * diffY;
      stdX += diffX * diffX;
      stdY += diffY * diffY;
    }
    
    const denominator = Math.sqrt(stdX * stdY);
    if (denominator === 0) return 0;
    
    return covariance / denominator;
  };

  // Calculate correlation matrix for all crypto pairs
  const calculateCorrelations = () => {
    const subset = cryptos.slice(0, displayCount);
    const matrix = [];
    
    for (let i = 0; i < subset.length; i++) {
      const row = [];
      for (let j = 0; j < subset.length; j++) {
        if (i === j) {
          row.push(1); // Perfect correlation with self
        } else {
          const correlation = calculatePearsonCorrelation(
            subset[i].sparkline,
            subset[j].sparkline
          );
          row.push(correlation);
        }
      }
      matrix.push(row);
    }
    
    setCorrelationMatrix(matrix);
  };

  // Get color based on correlation value
  const getCorrelationColor = (value, isCell = true) => {
    if (value === 1 && isCell) return 'rgba(100, 116, 139, 0.3)'; // Diagonal - gray
    
    if (value >= 0.8) return 'rgba(34, 197, 94, 0.8)';   // Strong positive - green
    if (value >= 0.5) return 'rgba(132, 204, 22, 0.7)'; // Moderate positive - lime
    if (value >= 0.2) return 'rgba(234, 179, 8, 0.6)';  // Weak positive - yellow
    if (value >= -0.2) return 'rgba(148, 163, 184, 0.4)'; // No correlation - gray
    if (value >= -0.5) return 'rgba(249, 115, 22, 0.6)'; // Weak negative - orange
    if (value >= -0.8) return 'rgba(239, 68, 68, 0.7)'; // Moderate negative - red
    return 'rgba(220, 38, 38, 0.8)'; // Strong negative - dark red
  };

  // Get text color for correlation value
  const getTextColor = (value) => {
    if (Math.abs(value) >= 0.5) return 'white';
    return '#e2e8f0';
  };

  // Get correlation interpretation
  const getCorrelationInterpretation = (value) => {
    if (value >= 0.8) return { label: 'Very Strong Positive', description: 'These assets tend to move together very closely' };
    if (value >= 0.5) return { label: 'Strong Positive', description: 'These assets often move in the same direction' };
    if (value >= 0.2) return { label: 'Weak Positive', description: 'These assets have a slight tendency to move together' };
    if (value >= -0.2) return { label: 'No Correlation', description: 'These assets move independently of each other' };
    if (value >= -0.5) return { label: 'Weak Negative', description: 'These assets have a slight tendency to move oppositely' };
    if (value >= -0.8) return { label: 'Strong Negative', description: 'These assets often move in opposite directions' };
    return { label: 'Very Strong Negative', description: 'These assets tend to move in opposite directions very closely' };
  };

  // Find strongest and weakest correlations (excluding self)
  const getNotableCorrelations = () => {
    const subset = cryptos.slice(0, displayCount);
    const pairs = [];
    
    if (!correlationMatrix.length || correlationMatrix.length !== subset.length) {
      return { strongest: [], weakest: [] };
    }
    
    for (let i = 0; i < correlationMatrix.length; i++) {
      if (!correlationMatrix[i]) continue;
      for (let j = i + 1; j < correlationMatrix[i].length; j++) {
        if (!subset[i] || !subset[j]) continue;
        pairs.push({
          crypto1: subset[i],
          crypto2: subset[j],
          correlation: correlationMatrix[i][j]
        });
      }
    }
    
    // Sort by absolute correlation
    pairs.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
    
    return {
      strongest: pairs.slice(0, 5),
      weakest: pairs.filter(p => Math.abs(p.correlation) < 0.3).slice(0, 5)
    };
  };

  // Only calculate if matrix matches current displayCount
  const subset = cryptos.slice(0, displayCount);
  const matrixReady = correlationMatrix.length === subset.length && 
                      correlationMatrix.every(row => row && row.length === subset.length);
  const notable = matrixReady ? getNotableCorrelations() : { strongest: [], weakest: [] };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-30px',
          right: '-30px',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Grid3X3 size={28} />
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Crypto Correlation Matrix</h1>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '0.9rem' }}>
                Understand how different cryptocurrencies move together
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setShowHelp(!showHelp)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1rem',
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                <HelpCircle size={16} />
                Help
              </button>
              <button
                onClick={fetchCryptoData}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1rem',
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                {loading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Help Panel */}
      {showHelp && (
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#a78bfa' }}>
            Understanding Correlation
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <h4 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem' }}>What is Correlation?</h4>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: '1.6', margin: 0 }}>
                Correlation measures how two assets move in relation to each other. 
                Values range from -1 (opposite movement) to +1 (same movement).
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem' }}>Why It Matters</h4>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: '1.6', margin: 0 }}>
                Understanding correlation helps with diversification. 
                Holding assets with low correlation can reduce portfolio volatility.
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.5rem' }}>Color Guide</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 16, height: 16, background: 'rgba(34, 197, 94, 0.8)', borderRadius: '2px' }} />
                  <span style={{ color: '#94a3b8' }}>Strong positive (+0.8 to +1.0)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 16, height: 16, background: 'rgba(234, 179, 8, 0.6)', borderRadius: '2px' }} />
                  <span style={{ color: '#94a3b8' }}>Weak positive (+0.2 to +0.5)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 16, height: 16, background: 'rgba(148, 163, 184, 0.4)', borderRadius: '2px' }} />
                  <span style={{ color: '#94a3b8' }}>No correlation (-0.2 to +0.2)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: 16, height: 16, background: 'rgba(239, 68, 68, 0.7)', borderRadius: '2px' }} />
                  <span style={{ color: '#94a3b8' }}>Negative (-0.5 to -1.0)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Educational Disclaimer */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(139, 92, 246, 0.1)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <AlertTriangle size={20} color="#a78bfa" style={{ flexShrink: 0, marginTop: '2px' }} />
        <p style={{ color: '#c4b5fd', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
          <strong>Educational Tool:</strong> Correlations are based on 7-day price data and change over time. 
          Past correlations don't guarantee future behavior. This is for educational purposes only.
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Loader size={48} color="#8b5cf6" className="spin" />
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.75rem', textAlign: 'center' }}>
          <p style={{ color: '#fca5a5' }}>{error}</p>
          <button onClick={fetchCryptoData}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#8b5cf6', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Show top:</span>
              {[5, 10, 15, 20].map(count => (
                <button
                  key={count}
                  onClick={() => setDisplayCount(count)}
                  disabled={cryptos.length < count}
                  style={{
                    padding: '0.5rem 1rem',
                    background: displayCount === count ? 'rgba(139, 92, 246, 0.3)' : 'rgba(30, 41, 59, 0.5)',
                    border: displayCount === count ? '1px solid rgba(139, 92, 246, 0.5)' : '1px solid #334155',
                    borderRadius: '0.5rem',
                    color: displayCount === count ? '#a78bfa' : '#94a3b8',
                    cursor: cryptos.length < count ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    opacity: cryptos.length < count ? 0.5 : 1
                  }}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
            
            {/* Correlation Matrix */}
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              border: '1px solid #334155',
              overflowX: 'auto'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BarChart3 size={20} color="#8b5cf6" />
                Correlation Heatmap (7-Day)
              </h3>
              
              <div style={{ overflowX: 'auto', paddingBottom: '0.5rem' }}>
                {!matrixReady ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Loader size={32} color="#8b5cf6" className="spin" />
                  </div>
                ) : (
                <table style={{ borderCollapse: 'collapse', margin: '0 auto' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '0.5rem', minWidth: '60px' }}></th>
                      {subset.map(crypto => (
                        <th key={crypto.id} style={{ padding: '0.25rem', textAlign: 'center', minWidth: '50px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                            <img src={crypto.image} alt={crypto.name} style={{ width: 20, height: 20, borderRadius: '50%' }} />
                            <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{crypto.symbol}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {subset.map((crypto, i) => (
                      <tr key={crypto.id}>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{crypto.symbol}</span>
                            <img src={crypto.image} alt={crypto.name} style={{ width: 20, height: 20, borderRadius: '50%' }} />
                          </div>
                        </td>
                        {correlationMatrix[i]?.map((value, j) => {
                          const targetCrypto = subset[j];
                          if (!targetCrypto) return null;
                          return (
                          <td
                            key={j}
                            onClick={() => i !== j && setSelectedPair({ crypto1: crypto, crypto2: targetCrypto, correlation: value })}
                            style={{
                              padding: '0.25rem',
                              textAlign: 'center',
                              background: getCorrelationColor(value),
                              cursor: i !== j ? 'pointer' : 'default',
                              borderRadius: '4px',
                              minWidth: '50px',
                              transition: 'transform 0.1s',
                            }}
                            onMouseEnter={(e) => i !== j && (e.currentTarget.style.transform = 'scale(1.1)')}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            <span style={{ 
                              fontSize: '0.7rem', 
                              fontWeight: '600',
                              color: getTextColor(value)
                            }}>
                              {value.toFixed(2)}
                            </span>
                          </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
              </div>
              
              {/* Legend */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginTop: '1rem',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>-1.0</span>
                <div style={{ 
                  display: 'flex', 
                  height: '12px', 
                  borderRadius: '6px', 
                  overflow: 'hidden',
                  width: '200px'
                }}>
                  <div style={{ flex: 1, background: 'rgba(239, 68, 68, 0.8)' }} />
                  <div style={{ flex: 1, background: 'rgba(249, 115, 22, 0.6)' }} />
                  <div style={{ flex: 1, background: 'rgba(148, 163, 184, 0.4)' }} />
                  <div style={{ flex: 1, background: 'rgba(234, 179, 8, 0.6)' }} />
                  <div style={{ flex: 1, background: 'rgba(132, 204, 22, 0.7)' }} />
                  <div style={{ flex: 1, background: 'rgba(34, 197, 94, 0.8)' }} />
                </div>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>+1.0</span>
              </div>
            </div>

            {/* Selected Pair Detail */}
            {selectedPair && (
              <div style={{
                background: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>Pair Analysis</h3>
                  <button
                    onClick={() => setSelectedPair(null)}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.25rem' }}
                  >
                    ×
                  </button>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <img src={selectedPair.crypto1.image} alt={selectedPair.crypto1.name} style={{ width: 48, height: 48, borderRadius: '50%' }} />
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>{selectedPair.crypto1.symbol}</div>
                  </div>
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: 'bold',
                    color: selectedPair.correlation >= 0 ? '#22c55e' : '#ef4444'
                  }}>
                    {selectedPair.correlation >= 0 ? '↔' : '⇄'}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <img src={selectedPair.crypto2.image} alt={selectedPair.crypto2.name} style={{ width: 48, height: 48, borderRadius: '50%' }} />
                    <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>{selectedPair.crypto2.symbol}</div>
                  </div>
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: 'bold',
                    color: getCorrelationColor(selectedPair.correlation, false)
                  }}>
                    {selectedPair.correlation.toFixed(3)}
                  </div>
                  <div style={{ 
                    display: 'inline-block',
                    padding: '0.375rem 1rem',
                    background: `${getCorrelationColor(selectedPair.correlation, false)}30`,
                    borderRadius: '2rem',
                    color: getCorrelationColor(selectedPair.correlation, false),
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    marginTop: '0.5rem'
                  }}>
                    {getCorrelationInterpretation(selectedPair.correlation).label}
                  </div>
                </div>
                
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center', margin: 0 }}>
                  {getCorrelationInterpretation(selectedPair.correlation).description}
                </p>
              </div>
            )}

            {/* Notable Correlations */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              {/* Strongest Correlations */}
              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '1px solid #334155'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={20} color="#22c55e" />
                  Most Correlated Pairs
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {notable.strongest.map((pair, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedPair(pair)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        background: 'rgba(15, 23, 42, 0.5)',
                        borderRadius: '0.5rem',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(51, 65, 85, 0.5)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(15, 23, 42, 0.5)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '-8px' }}>
                        <img src={pair.crypto1.image} alt="" style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #1e293b' }} />
                        <img src={pair.crypto2.image} alt="" style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #1e293b', marginLeft: '-8px' }} />
                      </div>
                      <span style={{ flex: 1, fontSize: '0.9rem' }}>
                        {pair.crypto1.symbol}/{pair.crypto2.symbol}
                      </span>
                      <span style={{ 
                        fontWeight: '600',
                        color: pair.correlation >= 0 ? '#22c55e' : '#ef4444'
                      }}>
                        {pair.correlation.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Diversification Opportunities */}
              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '1px solid #334155'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingDown size={20} color="#f59e0b" />
                  Best for Diversification
                </h3>
                {notable.weakest.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {notable.weakest.map((pair, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedPair(pair)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          background: 'rgba(15, 23, 42, 0.5)',
                          borderRadius: '0.5rem',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(51, 65, 85, 0.5)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(15, 23, 42, 0.5)'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <img src={pair.crypto1.image} alt="" style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #1e293b' }} />
                          <img src={pair.crypto2.image} alt="" style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #1e293b', marginLeft: '-8px' }} />
                        </div>
                        <span style={{ flex: 1, fontSize: '0.9rem' }}>
                          {pair.crypto1.symbol}/{pair.crypto2.symbol}
                        </span>
                        <span style={{ 
                          fontWeight: '600',
                          color: '#f59e0b'
                        }}>
                          {pair.correlation.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#64748b', fontSize: '0.9rem', textAlign: 'center', padding: '1rem' }}>
                    Most top cryptocurrencies are highly correlated during this period.
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default CryptoCorrelation;
